import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { reserveSection } from "../../Api/reservation";
import { useAppDispatch } from "../../app/store";
import { showToastError } from "../../hooks/useToast";
import {
  selectFlags,
  selectMobileFeatures,
  selectOfertaInvalidandoOVenciendo,
  setInTransitInscriptionRequest,
} from "../../reduxSlices/globalFlagsSlice";
import { selectStudent } from "../../reduxSlices/studentSlice";
import Translatable from "../Translatable/Translatable";
import {
  addSelectedOfferedSubject,
  removeSelectedOfferedSubject,
  selectInscribedSubjects,
  selectSearchSubjectsValue,
  selectSelectedFilterTagsList,
  selectSubjectsQuotas,
  selectSubcriptionProcessSectionIds,
  setLoadingRequestInTransit,
} from "../../reduxSlices/subjectsSlice";
import { IOfferedSubject, IQuota, IReserveError, ISelectedSection } from "../../types";
import { getCourseQuota } from "../../utils/quota";
import { filterAndOrderSubjects } from "../../utils/subjects";
import DesktopSubjectItem from "../DesktopSubjectItem/DesktopSubjectItem";
import "./OfferSubjectsVirtualizedList.css";
import usePinnedSubjects from "../../hooks/usePinnedSubjects";

interface Props {
  subjects: IOfferedSubject[];
  virtualListRef: React.MutableRefObject<List<any> | null>;
  handleScroll: (data: any) => void;
}

function OfferSubjectsVirtualizedList({ subjects, virtualListRef, handleScroll }: Props) {
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const inscribedSubjects = useSelector(selectInscribedSubjects);
  const student = useSelector(selectStudent);
  const subjectsQuotas = useSelector(selectSubjectsQuotas);
  const listadoFiltros = useSelector(selectSelectedFilterTagsList);
  const cajaBusqueda = useSelector(selectSearchSubjectsValue);
  const mobileFeatures = useSelector(selectMobileFeatures);
  const { collapseCards, collapseCardsSinCupo } = useSelector(selectFlags);
  const subcriptionProcessSectionIds = useSelector(selectSubcriptionProcessSectionIds);
  const inscribedSubsIds = Array.isArray(inscribedSubjects)
    ? inscribedSubjects.map((s) => ({ idCurso: s.idCurso, anioLectivo: s.anioLectivo }))
    : [];
  const [reserveError, setReserveError] = useState<IReserveError>({});
  const [selectedSection, setSelectedSection] = useState<ISelectedSection>();

  const { isCoursePinned } = usePinnedSubjects();

  const subjectsOrdered = Array.isArray(subjects)
    ? filterAndOrderSubjects(
        subjects,
        inscribedSubsIds,
        student.debeFiltrarCursosConCupoNoAsignado,
        !!student.prioridad,
      )
    : [];

  // Filtrar cursos que NO están en proceso para la lista virtualizada
  const subjectsNotInProcess = subjectsOrdered.filter(
    (subject) =>
      !subcriptionProcessSectionIds.some(
        (item) => item.idCurso === subject.idCurso && item.anioLectivo === subject.anioLectivo,
      ),
  );

  useEffect(() => {
    if (reserveError && selectedSection) {
      const keys = Object.keys(reserveError);
      if (keys.length && keys.includes(selectedSection?.idC.toString())) {
        setSelectedSection(undefined);
      }
    }
  }, [reserveError]);

  /* -------------------------------------------------------------------------- */
  /*                              METHODS                                       */
  /* -------------------------------------------------------------------------- */

  const handleReserve = async (section?: ISelectedSection) => {
    let result: any = {};
    if (selectedSection) {
      result = await onReserve(selectedSection);
      setOpen(false);
    } else if (section) {
      result = await onReserve(section);
    }
    return result;
  };

  const onReserve = async (selectedSection: ISelectedSection) => {
    let reserveResult: any = {};
    try {
      dispatch(setInTransitInscriptionRequest(true));
      if (selectedSection) {
        const results = await reserveSection({
          idC: selectedSection.idC,
          idS: selectedSection.idS,
          idA: selectedSection.idA,
          tim: selectedSection.tim,
          tic: selectedSection.tic,
          aL: selectedSection.aL,
          idPersona: Number(student.id),
          idMateria: selectedSection.idMateria,
        });

        reserveResult = results.data;

        if (results.data.responseCode !== 200) {
          dispatch(removeSelectedOfferedSubject(selectedSection.idS));
          setReserveError({
            ...reserveError,
            [selectedSection.idC]: results.data.responseMessage,
          });
          showToastError(results.data.responseMessage);
          setReserveError((prevState) => {
            const newState = prevState;
            delete newState[selectedSection.idC];
            return newState;
          });
        } else {
          dispatch(addSelectedOfferedSubject(selectedSection.idS));

          await dispatch(setLoadingRequestInTransit(true));
        }
      }
    } catch (error) {
      console.log("error :>> ", error);
      reserveResult = error;
    } finally {
      dispatch(setInTransitInscriptionRequest(false));
    }
    return reserveResult;
  };

  /* -------------------------------------------------------------------------- */
  /*                              RENDER COMPONENTS                             */
  /* -------------------------------------------------------------------------- */
  const emptyQuota = (quotaId: number): IQuota => {
    return {
      qty: 0,
      availability: 0,
      quotaId: quotaId,
      cursoId: -1,
      anioLectivo: -1,
      readyToUse: true,
      lastUpdateTimestamp: 0,
    };
  };
  const Row = useCallback(
    ({ index, style }: any) => {
      // Validar que subjectsNotInProcess sea un array y que el índice sea válido
      if (
        !Array.isArray(subjectsNotInProcess) ||
        index >= subjectsNotInProcess.length ||
        !subjectsNotInProcess[index]
      ) {
        return <div style={style}></div>;
      }

      const s = subjectsNotInProcess[index];
      const idCupo = s.subjectData?.reserveData?.idC || -1;
      return (
        <div style={style}>
          <DesktopSubjectItem
            idMateria={s.idMateria}
            id={s.idCurso}
            key={`${s.idCurso}-${s.anioLectivo}-${index}`}
            subjectId={s.idMateria}
            section={s.section}
            subjectData={s.subjectData}
            quota={idCupo !== -1 ? getCourseQuota(subjectsQuotas, idCupo) : emptyQuota(idCupo)}
            onChange={handleReserve}
            pinned={isCoursePinned(s.idCurso, s.anioLectivo)}
            creditos={s.creditos}
            sectionType={s.tipoMateria}
            turno={s.turno}
            subject={s}
            disableAnimations={true}
          />
        </div>
      );
    },
    [subjectsNotInProcess, subjectsQuotas, isCoursePinned],
  );

  const handleVirtualCardHeight = (index: number): number => {
    // Validar que subjectsNotInProcess sea un array y que el índice sea válido
    if (!Array.isArray(subjectsNotInProcess) || index >= subjectsNotInProcess.length || !subjectsNotInProcess[index]) {
      return 200; // Altura por defecto
    }

    const s = subjectsNotInProcess[index];
    const quota = getCourseQuota(subjectsQuotas, s.subjectData.reserveData.idC);
    const mobileSizeNormal = 230;
    const mobileSizeCollapsed = 150;
    const desktopSizeNormal = 200;
    const desktopSizeCollapsed = 80;

    if ((collapseCardsSinCupo && quota && quota.availability <= 0) || collapseCards) {
      if (mobileFeatures) {
        return mobileSizeCollapsed;
      } else {
        return desktopSizeCollapsed;
      }
    } else {
      if (mobileFeatures) {
        return mobileSizeNormal;
      } else {
        return desktopSizeNormal;
      }
    }
  };

  if (!Array.isArray(subjectsNotInProcess) || subjectsNotInProcess.length === 0) {
    return (
      <div className="listContainer">
        {(listadoFiltros?.length || cajaBusqueda) && (
          <div className="bg-[#F2F2F2] h-[200px] max-h-[200px] w-full rounded-[8px] flex flex-col justify-center items-center">
            <SlIcon name="search" style={{ fontSize: "24px" }}></SlIcon>
            <p className="text-[#8c8c8c]">
              <Translatable as="span">No encontramos cursos para tu búsqueda.</Translatable>
            </p>
          </div>
        )}
        {!(listadoFiltros?.length || cajaBusqueda) && (
          <div className="bg-[#f5f5f5] h-[230px] max-h-[230px] md:h-[200px] md:max-h-[200px] w-full rounded-[8px] flex flex-col justify-center items-center border-[1px] border-dashed border-[#bebebe]">
            <SlIcon name="dash-circle" style={{ fontSize: "24px" }} className="text-[#B7B7B7]"></SlIcon>
            <p className="text-[#8c8c8c] mt-2">
              <Translatable as="span">No hay cursos disponibles.</Translatable>
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ flex: "1 1 auto" }}>
      {collapseCards && (
        <AutoSizer>
          {({ height, width }: any) => (
            <List
              ref={(ref) => (virtualListRef.current = ref)}
              onScroll={handleScroll}
              className="List"
              height={height}
              width={width}
              itemCount={subjectsNotInProcess.length}
              itemSize={(index) => handleVirtualCardHeight(index)}
              itemKey={(index) => subjectsNotInProcess[index].idCurso}
              layout="vertical"
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      )}
      {collapseCardsSinCupo && (
        <AutoSizer>
          {({ height, width }: any) => (
            <List
              ref={(ref) => (virtualListRef.current = ref)}
              onScroll={handleScroll}
              className="List"
              height={height}
              width={width}
              itemCount={subjectsNotInProcess.length}
              itemSize={(index) => handleVirtualCardHeight(index)}
              itemKey={(index) => subjectsNotInProcess[index].idCurso}
              layout="vertical"
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      )}
      {!collapseCardsSinCupo && !collapseCards && (
        <AutoSizer>
          {({ height, width }: any) => (
            <List
              ref={(ref) => (virtualListRef.current = ref)}
              onScroll={handleScroll}
              className="List"
              height={height}
              width={width}
              itemCount={subjectsNotInProcess.length}
              itemSize={(index) => handleVirtualCardHeight(index)}
              itemKey={(index) => subjectsNotInProcess[index].idCurso}
              layout="vertical"
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      )}
      {/* {selectedSection && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          message={`¿Desea inscribirse en el curso ${selectedSection.section}?`}
          okButton={{
            label: "Si",
            buttonFn: handleReserve,
          }}
          closeButton={{
            label: "No",
            buttonFn: () => handleRefuse(selectedSection.idS),
          }}
        />
      )} */}
    </div>
  );
}

export default OfferSubjectsVirtualizedList;
